import {
  Autocomplete,
  Chip,
  CircularProgress,
  TextField,
  TextFieldProps,
} from "@mui/material";
import useGlobalContext from "hooks/globalData";
import { useIntl } from "react-intl";
import { useQuery } from "react-query";
import { NotificationConfigurationStatus } from "services/KnowfullyAdminClient";

type SelectNotificationStatusProps = {
  value: string[];
  onChange: (ids: string[]) => void;
  label?: string;
  disabled?: boolean;
  textFieldProps?: Partial<TextFieldProps>;
};

const SelectNotificationStatus = ({
  value,
  onChange,
  label,
  disabled,
  textFieldProps,
}: SelectNotificationStatusProps) => {
  const { formatMessage: t } = useIntl();
  const { notificationsConfigurationsService } = useGlobalContext();

  const { data, isLoading } = useQuery(
    ["notificationsConfigurationsService.getNotificationConfigurationStatuses"],
    () =>
      notificationsConfigurationsService.getNotificationConfigurationStatuses(),
  );

  const options = data?.data ?? [];

  const selectedValues = Array.isArray(value)
    ? options.filter((o) => value.includes(String(o.id)))
    : [];

  return (
    <Autocomplete
      multiple
      autoComplete
      disableCloseOnSelect
      limitTags={3}
      loading={isLoading}
      disabled={disabled || isLoading}
      options={options}
      value={selectedValues}
      getOptionLabel={(option) =>
        (option as NotificationConfigurationStatus).name || ""
      }
      isOptionEqualToValue={(option, val) => option.id === val.id}
      onChange={(_event, newValue) => {
        onChange(
          (newValue as NotificationConfigurationStatus[])
            .map((v) => String(v.id))
            .filter(Boolean),
        );
      }}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip
            size="small"
            label={(option as NotificationConfigurationStatus).name}
            {...getTagProps({ index })}
            key={(option as NotificationConfigurationStatus).id}
          />
        ))
      }
      renderInput={(input) => (
        <TextField
          variant="outlined"
          {...textFieldProps}
          {...input}
          label={label || t({ id: "statusLabel" })}
          InputProps={{
            ...textFieldProps?.InputProps,
            ...input.InputProps,
            endAdornment: (
              <>
                {isLoading ? <CircularProgress size={16} /> : null}
                {input.InputProps.endAdornment}
              </>
            ),
          }}
          fullWidth
        />
      )}
    />
  );
};

export default SelectNotificationStatus;
